import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Smartphone, UserX, Users } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { PAGE_HELP } from '@/shared/config/page-help';
import { useSelection } from '@/shared/lib/use-selection';
import { quantityLabel, shouldShowLeads, isMultiStoreLeads, passbookCurrency } from '@/shared/lib/passbook-utils';
import { decimal } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { PageHeader } from '@/shared/components/page-header';
import { FloatingActionBar } from '@/shared/components/floating-action-bar';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { MobileFiltersDialog, MobileFiltersTrigger } from '@/shared/components/mobile-filters';
import { SortSelect } from '@/shared/components/sort-select';
import { StoreFilterSelect } from '@/shared/components/store-filter-select';
import { InstalledFilterSelect, type InstalledFilter } from '@/shared/components/installed-filter-select';
import { PageSizeSelect } from '@/shared/components/page-size-select';
import { KpiCard } from '@/shared/components/kpi-card';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { Pagination } from '@/shared/ui/pagination';
import { DatePicker } from '@/shared/ui/date-picker';
import type { Lead } from '@/shared/types';
import type { Store } from '@/shared/types/store';
import { useLeadsFetcher, fetchLeadsStats, type LeadsStats } from '../api/use-leads';
import { useStores } from '../api/use-stores';
import { exportLeadsCsv } from '../model/export-leads';
import { LeadCard } from './lead-card';
import { LeadEditDialog } from './lead-edit-dialog';
import { ExportDialog } from './export-dialog';

const SORT_OPTIONS = [
  { value: 'updated:desc', label: 'Última atualização' },
  { value: 'updated:asc', label: 'Atualização: mais antigos' },
  { value: 'createdAt:desc', label: 'Cadastro: mais recentes' },
  { value: 'createdAt:asc', label: 'Cadastro: mais antigos' },
  { value: 'profile.name:asc', label: 'Nome A→Z' },
  { value: 'profile.name:desc', label: 'Nome Z→A' },
  { value: 'total_points:desc', label: 'Maior saldo' },
  { value: 'total_points:asc', label: 'Menor saldo' },
];

function parseSortOption(value: string) {
  const [id, dir] = value.split(':');
  return [{ id, desc: dir === 'desc' }];
}

const DEFAULT_SORT = 'updated:desc';
const DEFAULT_PAGE_SIZE = 25;

interface LeadsFiltersFieldsProps {
  showStoreFilter: boolean;
  storeFilterByBranchId?: boolean;
  storeFilter: string;
  stores: Store[];
  storesLoading: boolean;
  onStoreFilterChange: (value: string) => void;
  installedFilter: InstalledFilter;
  onInstalledFilterChange: (value: InstalledFilter) => void;
  createdFrom: string;
  onCreatedFromChange: (value: string) => void;
  createdTo: string;
  onCreatedToChange: (value: string) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  stacked?: boolean;
}

function LeadsFiltersFields({
  showStoreFilter,
  storeFilterByBranchId,
  storeFilter,
  stores,
  storesLoading,
  onStoreFilterChange,
  installedFilter,
  onInstalledFilterChange,
  createdFrom,
  onCreatedFromChange,
  createdTo,
  onCreatedToChange,
  sortValue,
  onSortChange,
  pageSize,
  onPageSizeChange,
  stacked = false,
}: LeadsFiltersFieldsProps) {
  return (
    <div
      className={cn(
        'grid gap-2',
        stacked
          ? 'grid-cols-1'
          : showStoreFilter
            ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6'
            : 'grid-cols-2 md:grid-cols-3 xl:grid-cols-5',
      )}
    >
      {showStoreFilter ? (
        <StoreFilterSelect
          value={storeFilter}
          stores={stores}
          onChange={onStoreFilterChange}
          disabled={storesLoading}
          className="w-full"
          valueFrom={storeFilterByBranchId ? 'branchId' : 'name'}
        />
      ) : null}
      <InstalledFilterSelect value={installedFilter} onChange={onInstalledFilterChange} className="w-full" />
      <DatePicker
        value={createdFrom}
        onChange={onCreatedFromChange}
        placeholder="Data início"
        className="w-full"
        toDate={createdTo ? new Date(`${createdTo}T23:59:59`) : undefined}
      />
      <DatePicker
        value={createdTo}
        onChange={onCreatedToChange}
        placeholder="Data fim"
        className="w-full"
        fromDate={createdFrom ? new Date(`${createdFrom}T00:00:00`) : undefined}
      />
      <SortSelect value={sortValue} options={SORT_OPTIONS} onChange={onSortChange} className="w-full" />
      <PageSizeSelect value={pageSize} onChange={onPageSizeChange} className="w-full" />
    </div>
  );
}

export function LeadsPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const fetchData = useLeadsFetcher(idPassbook, passbook, undefined, user);

  const [rows, setRows] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortValue, setSortValue] = useState('updated:desc');
  const [storeFilter, setStoreFilter] = useState('');
  const [installedFilter, setInstalledFilter] = useState<InstalledFilter>('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editLead, setEditLead] = useState<Lead | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [stats, setStats] = useState<LeadsStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const selection = useSelection(rows);

  const labelQtde = quantityLabel(passbook);
  const isCashback = Boolean(passbook.hasCashback);
  const moneyCode = passbookCurrency(passbook);
  const hasMultiStore = isMultiStoreLeads(passbook);
  // Legado: só Client/Admin veem dropdown de loja; Operator fica preso à position.
  const showStoreFilter = hasMultiStore && user?.role !== 'Operator';
  const storeFilterByBranchId =
    Boolean(passbook.hasMultiStore) &&
    passbook.partner === false &&
    passbook.partnerName === 'webautomacao';
  const canSeeEmail = user ? ['Client', 'Operator', 'Admin', 'Owner'].includes(user.role) : false;
  const { stores, isLoading: storesLoading, resolveLeadStores } = useStores(idPassbook, hasMultiStore);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPageIndex(0);
    selection.clear();
  }, [debouncedSearch, idPassbook, sortValue, storeFilter, installedFilter, createdFrom, createdTo, pageSize]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchData({
      pageIndex,
      pageSize,
      sorting: parseSortOption(sortValue),
      search: debouncedSearch,
      storeFilter: storeFilter || undefined,
      installedFilter: installedFilter || undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    })
      .then((result) => {
        if (!cancelled) {
          setRows(result.rows);
          setTotal(result.total);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageIndex, pageSize, debouncedSearch, sortValue, storeFilter, installedFilter, createdFrom, createdTo, fetchData, idPassbook, listKey]);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    fetchLeadsStats(idPassbook, passbook, user)
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => { if (!cancelled) setStats(null); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [idPassbook, passbook, user]);

  const exportSelected = useCallback(() => {
    exportLeadsCsv(selection.selected, passbook, { uniqueIdLabel: passbook.fieldUniqueIdLabel ?? 'CPF', canSeeEmail });
    toast.success(`${selection.count} lead${selection.count > 1 ? 's' : ''} exportado${selection.count > 1 ? 's' : ''}.`);
  }, [selection.selected, selection.count, passbook, canSeeEmail]);

  const refreshList = useCallback(() => {
    setListKey((k) => k + 1);
    selection.clear();
  }, [selection]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (storeFilter) n += 1;
    if (installedFilter) n += 1;
    if (createdFrom) n += 1;
    if (createdTo) n += 1;
    if (sortValue !== DEFAULT_SORT) n += 1;
    if (pageSize !== DEFAULT_PAGE_SIZE) n += 1;
    return n;
  }, [storeFilter, installedFilter, createdFrom, createdTo, sortValue, pageSize]);

  const clearFilters = useCallback(() => {
    setStoreFilter('');
    setInstalledFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setSortValue(DEFAULT_SORT);
    setPageSize(DEFAULT_PAGE_SIZE);
  }, []);

  const filterFieldsProps: LeadsFiltersFieldsProps = {
    showStoreFilter,
    storeFilterByBranchId,
    storeFilter,
    stores,
    storesLoading,
    onStoreFilterChange: setStoreFilter,
    installedFilter,
    onInstalledFilterChange: setInstalledFilter,
    createdFrom,
    onCreatedFromChange: setCreatedFrom,
    createdTo,
    onCreatedToChange: setCreatedTo,
    sortValue,
    onSortChange: setSortValue,
    pageSize,
    onPageSizeChange: setPageSize,
  };

  if (!user || !shouldShowLeads(passbook, user.role)) {
    return <Navigate to={`/loja/${idPassbook}/operador`} replace />;
  }

  const showSkeleton = isLoading;

  return (
    <>
      <PageHeader
        title="Leads"
        help={PAGE_HELP.leads}
        description={`Clientes — ${passbook.organizationName}`}
        actions={
          <Tooltip content="Exportar base ou logs por e-mail" side="bottom">
            <Button variant="secondary" onClick={() => setExportOpen(true)}>
              <Download className="size-4" /> Exportar
            </Button>
          </Tooltip>
        }
      />

      <div className="mb-6 grid gap-4 grid-cols-2 sm:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Total de Leads" value={statsLoading ? '…' : decimal(stats?.total ?? total)} icon={Users} loading={statsLoading} index={0} />
        <KpiCard label="Instalados" value={statsLoading ? '…' : decimal(stats?.installed ?? 0)} icon={Smartphone} loading={statsLoading} index={1} />
        <KpiCard label="Não instalados" value={statsLoading ? '…' : decimal(stats?.notInstalled ?? 0)} icon={UserX} loading={statsLoading} index={2} />
        <KpiCard label="Instalados (iOS)" value={statsLoading ? '…' : decimal(stats?.ios ?? 0)} icon={Smartphone} loading={statsLoading} index={3} />
        <KpiCard label="Instalados (Android)" value={statsLoading ? '…' : decimal(stats?.android ?? 0)} icon={Smartphone} loading={statsLoading} index={4} />
        <KpiCard
          label="Taxa de instalação"
          value={
            statsLoading || !stats?.total
              ? '…'
              : `${Math.round(((stats.installed ?? 0) / stats.total) * 100)}%`
          }
          icon={Smartphone}
          loading={statsLoading}
          index={5}
        />
      </div>

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome, CPF, e-mail…"
        searchAside={
          <MobileFiltersTrigger activeCount={activeFilterCount} onClick={() => setFiltersOpen(true)} />
        }
        filtersRow={
          <div className="hidden sm:block">
            <LeadsFiltersFields {...filterFieldsProps} />
          </div>
        }
        selectAllId="select-all-leads"
        itemCount={rows.length}
        allOnPageSelected={selection.allOnPageSelected}
        someOnPageSelected={selection.someOnPageSelected}
        onToggleAllOnPage={selection.toggleAllOnPage}
        isLoading={showSkeleton}
        isEmpty={!isLoading && rows.length === 0}
        emptyTitle="Nenhum lead encontrado"
        emptyDescription="Tente outro termo de busca ou aguarde novos cadastros."
        footer={
          total > 0 ? (
            <Pagination
              page={pageIndex}
              pageSize={pageSize}
              total={total}
              onPageChange={setPageIndex}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {rows.map((lead, index) => (
          <li key={lead._id}>
            <LeadCard
              lead={lead}
              index={index}
              selected={selection.selectedIds.has(lead._id)}
              onToggle={selection.toggle}
              selectionMode={selection.count > 0}
              onEdit={setEditLead}
              idPassbook={idPassbook}
              labelQtde={labelQtde}
              isCashback={isCashback}
              currencyCode={moneyCode}
              canSeeEmail={canSeeEmail}
              uniqueIdLabel={passbook.fieldUniqueIdLabel ?? 'CPF'}
              hasMultiStore={hasMultiStore}
              resolveLeadStores={hasMultiStore ? resolveLeadStores : undefined}
              tiers={passbook.hasTiers ? passbook.tiers : undefined}
            />
          </li>
        ))}
      </SelectableListLayout>

      <MobileFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        activeCount={activeFilterCount}
        onClear={clearFilters}
        description="Ajuste ordenação, instalação, datas e paginação."
      >
        <LeadsFiltersFields {...filterFieldsProps} stacked />
      </MobileFiltersDialog>

      <FloatingActionBar count={selection.count} onClear={selection.clear}>
        <Tooltip content="Exportar os leads selecionados em CSV" side="top">
          <Button size="sm" variant="secondary" onClick={exportSelected}>
            <Download className="size-4" />
            Exportar selecionados
          </Button>
        </Tooltip>
      </FloatingActionBar>

      <LeadEditDialog
        lead={editLead}
        idPassbook={idPassbook}
        uniqueIdLabel={passbook.fieldUniqueIdLabel ?? 'CPF'}
        onClose={() => setEditLead(null)}
        onSaved={refreshList}
      />
      <ExportDialog idPassbook={idPassbook} open={exportOpen} onOpenChange={setExportOpen} />
    </>
  );
}
